import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { downloadLinksApi } from '~/entities/download-link/api/download-links'
import { useSupabaseSession } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'

const MINUTE = 60_000

export function useDownloadLinks() {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_DOWNLOAD_LINKS),
    queryFn: () => downloadLinksApi.list(),
    enabled: !!session && storageManager.isAdmin(),
    refetchInterval: MINUTE,
  })
}

function useDownloadLinkMutation<TInput>(
  mutationFn: (input: TInput) => Promise<unknown>,
  successMessage: () => string,
  logLabel: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ADMIN_DOWNLOAD_LINKS) })
      toast.success(successMessage())
    },
    onError: (error: unknown) => {
      log.error(error as Error, `[DownloadLinks] ${logLabel} failed`)
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useCreateDownloadLink() {
  return useDownloadLinkMutation(
    (name: string) => downloadLinksApi.create(name),
    m.downloadLinksCreated,
    'Create',
  )
}

export function useArchiveDownloadLink() {
  return useDownloadLinkMutation(
    (id: string) => downloadLinksApi.archive(id),
    m.downloadLinksArchived,
    'Archive',
  )
}
